import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, sharedGet } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Bell, MessageCircle, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Bell that polls unread chat (nutritionist ↔ client) + friend messages + feed
// Shows toast on new items, badge shows total count.
// Clients: opens a notification POPOVER (list of unread friend messages).
// Nutritionists: still links straight to the chat inbox.
export default function NotificationBell() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [chatUnread, setChatUnread] = useState(0);
  const [friendUnread, setFriendUnread] = useState(0);
  const [friendMsgs, setFriendMsgs] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [open, setOpen] = useState(false);
  const seenFeedIdsRef = useRef(new Set());
  const seenFriendMsgIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const isNutritionist = user?.role === "nutritionist";
  const isSuperAdmin = user?.role === "super_admin";
  const [openReports, setOpenReports] = useState(0);
  const seenReportIdsRef = useRef(new Set());

  const poll = async () => {
    try {
      if (isSuperAdmin) {
        // Super-admin: open moderation reports (block/report flow).
        try {
          const { data } = await api.get("/admin/moderation/reports", { params: { status: "open", limit: 20 } });
          setOpenReports(data.open_count || 0);
          const reports = data.reports || [];
          if (initializedRef.current) {
            reports.filter((r) => !seenReportIdsRef.current.has(r.report_id)).slice(0, 3).forEach((r) => {
              toast(`🚩 ${t("moderation.newReportToast", { reason: t(`moderation.reason_${r.reason}`), name: r.target?.name || "?" })}`, {
                duration: 8000,
                action: { label: t("admin.tab_moderation"), onClick: () => { window.location.href = "/admin?tab=moderation"; } },
              });
            });
          }
          reports.forEach((r) => seenReportIdsRef.current.add(r.report_id));
        } catch {}
      } else if (isNutritionist) {
        // Nutritionist ↔ client chat unread — clients no longer have this chat.
        try {
          const { data: uc } = await api.get("/chat/unread-count");
          setChatUnread(uc.count || 0);
        } catch {}
        try {
          const { data: feed } = await api.get("/nutritionist/feed?days=1");
          const newItems = feed.filter((f) => !seenFeedIdsRef.current.has(f.id));
          if (initializedRef.current) {
            newItems.slice(0, 3).forEach((it) => {
              const label = it.type === "chat" ? `💬 ${it.client_name}: ${it.title.slice(0, 60)}`
                : it.type === "meal" ? `🍽️ ${it.client_name} ha loggato: ${it.title.slice(0, 60)}`
                : it.type === "weight" ? `⚖️ ${it.client_name}: ${it.title}`
                : it.type === "progress_photo" ? `📸 ${it.client_name}: nuova foto progresso`
                : `${it.client_name}: ${it.title.slice(0, 50)}`;
              toast(label, { duration: 6000 });
            });
          }
          feed.forEach((f) => seenFeedIdsRef.current.add(f.id));
        } catch {}
      } else {
        // Client: poll friend messages
        try {
          const { data: fr } = await sharedGet("/client/friends/unread");
          setFriendUnread(fr.count || 0);
          const msgs = fr.messages || [];
          setFriendMsgs(msgs);
          if (initializedRef.current) {
            // Show a toast for each newly-seen unread message (up to 3 at once)
            const fresh = msgs.filter((m) => m.message_id && !seenFriendMsgIdsRef.current.has(m.message_id));
            fresh.slice(0, 3).reverse().forEach((m) => {
              toast(
                `${m.friend_name}: ${m.text || t("friends.chatEmpty") || "…"}`,
                {
                  description: t("friends.toastGoToChat"),
                  duration: 7000,
                  icon: "💬",
                  action: {
                    label: t("friends.openChat") || "Apri",
                    onClick: () => { window.location.href = `/app/amici?friend=${m.friend_id}`; },
                  },
                },
              );
            });
          }
          msgs.forEach((m) => m.message_id && seenFriendMsgIdsRef.current.add(m.message_id));
        } catch {}
      }
      initializedRef.current = true;
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    poll();
    const iv = setInterval(poll, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const totalUnread = chatUnread + friendUnread + openReports;

  const badge = totalUnread > 0 && (
    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#E8951C] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
      {totalUnread > 9 ? "9+" : totalUnread}
    </span>
  );

  // Super-admin: bell jumps straight to the Moderation tab.
  if (isSuperAdmin) {
    return (
      <Link to="/admin?tab=moderation" data-testid="notification-bell" title={t("moderation.openReports", { n: openReports })}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition">
        <Bell className={`h-4 w-4 ${openReports > 0 ? "text-[#E8951C]" : "text-slate-600 dark:text-slate-300"}`} />
        {badge}
      </Link>
    );
  }

  // Nutritionist keeps the direct link to the chat inbox.
  if (isNutritionist) {
    return (
      <Link to="/nutrizionista/chat" data-testid="notification-bell"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition">
        <Bell className="h-4 w-4 text-slate-600 dark:text-slate-300" />
        {badge}
      </Link>
    );
  }

  const localeMap = { it: "it-IT", en: "en-GB", de: "de-DE", fr: "fr-FR" };
  const locale = localeMap[lang] || "en-GB";
  const fmtWhen = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const sameDay = d.toDateString() === new Date().toDateString();
      return sameDay
        ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString(locale, { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  const GOAL_ICONS = {
    water_goal: "💧", steps_goal: "🚶", sleep_7h: "😴",
    diary_complete: "✅", plate_check_80: "🥗",
  };
  const achView = (a) => {
    if (a.type === "level_up") return {
      icon: a.emoji || "🏆",
      text: t("notifBell.levelUp", { level: a.level, name: t(`rewards.states.${a.level_key}.name`) }),
    };
    if (a.type === "streak") return {
      icon: "🔥",
      text: t("notifBell.streak", { days: a.days, points: a.points }),
    };
    if (a.type === "badge") return {
      icon: a.icon || "🏅",
      text: t("notifBell.badge", { name: t(`rewards.badgeNames.${a.badge_key}`) }),
    };
    return {
      icon: GOAL_ICONS[a.action] || "🎯",
      text: t(`notifBell.goals.${a.action}`) + (a.points ? ` · +${a.points} SP` : ""),
    };
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      api.get("/client/gamification/achievements?days=7")
        .then(({ data }) => setAchievements(data?.items || []))
        .catch(() => {});
    }
  };

  const hasContent = friendMsgs.length > 0 || achievements.length > 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-bell"
          aria-label={t("notifBell.title")}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition">
          <Bell className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-2xl overflow-hidden z-[70]" align="end" data-testid="notification-popover">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <p className="text-sm font-semibold text-[#2C5254] dark:text-slate-100">{t("notifBell.title")}</p>
        </div>
        {!hasContent ? (
          <div className="px-4 py-8 text-center" data-testid="notification-empty">
            <Bell className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{t("notifBell.empty")}</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {friendMsgs.map((m, i) => (
              <button
                key={m.message_id || i}
                type="button"
                data-testid={`notification-item-${i}`}
                onClick={() => { setOpen(false); navigate(`/app/amici?friend=${m.friend_id}`); }}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 active:scale-[0.99] transition"
              >
                <span className="h-9 w-9 rounded-full bg-[#5A8B8F]/15 text-[#2C5254] dark:text-slate-200 flex items-center justify-center shrink-0 text-sm font-bold">
                  {(m.friend_name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{m.friend_name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{fmtWhen(m.created_at)}</span>
                  </span>
                  <span className="block text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                    <MessageCircle className="h-3 w-3 shrink-0" /> {m.text || "…"}
                  </span>
                </span>
              </button>
            ))}
            {achievements.length > 0 && (
              <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold" data-testid="notification-achievements-header">
                {t("notifBell.achievements")}
              </p>
            )}
            {achievements.map((a, i) => {
              const v = achView(a);
              return (
                <button
                  key={`${a.type}-${a.ts}-${i}`}
                  type="button"
                  data-testid={`notification-achievement-${i}`}
                  onClick={() => { setOpen(false); navigate("/app/premi"); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 active:scale-[0.99] transition"
                >
                  <span className="h-9 w-9 rounded-full bg-[#E8951C]/12 flex items-center justify-center shrink-0 text-base">
                    {v.icon}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{v.text}</span>
                  </span>
                  <span className="text-[10px] text-slate-400 shrink-0">{fmtWhen(a.ts)}</span>
                </button>
              );
            })}
          </div>
        )}
        <button
          type="button"
          data-testid="notification-open-friends"
          onClick={() => { setOpen(false); navigate("/app/amici"); }}
          className="w-full px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 text-xs font-semibold text-[#2C5254] dark:text-slate-200 flex items-center justify-center gap-1 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
        >
          {t("notifBell.openFriends")} <ChevronRight className="h-3 w-3" />
        </button>
      </PopoverContent>
    </Popover>
  );
}
